import PropTypes from "prop-types";
import { Card } from "antd";
import "./Styles.css";
import dayjs from "dayjs";

function calcAge(birthday) {
  if (!birthday) return null;
  const b = dayjs(birthday);
  if (!b.isValid()) return null;
  return dayjs().diff(b, "year");
}

function calcBMI(weightKg, heightCm) {
  const w = Number(weightKg);
  const hCm = Number(heightCm);

  if (!Number.isFinite(w) || !Number.isFinite(hCm) || w <= 0 || hCm <= 0) return null;

  const hM = hCm / 100;
  const bmi = w / (hM * hM);
  return Number.isFinite(bmi) ? bmi.toFixed(1) : null;
}

function formatDate(value) {
  if (!value) return null;
  const d = dayjs(value);
  return d.isValid() ? d.format("YYYY-MM-DD") : null;
}

function formatPatientStatus(status, t) {
  const normalized = String(status || "").trim().toLowerCase();
  if (!normalized) return t("info_not_available", "N/A");
  if (normalized === "active") return t("option_active", "ACTIVE");
  if (normalized === "waiting") return t("option_waiting", "WAITING");
  if (normalized === "paused") return t("option_paused", "PAUSED");
  if (normalized === "completed") return t("option_completed", "COMPLETED");
  if (normalized === "abort" || normalized === "abandoned") {
    return t("option_abort", "ABANDONED");
  }
  return status;
}

export default function InfoCard({
  patient,
  programName,
  t,
  evaluationT0,
  evaluationT1,
  plannedEvaluationT0,
  plannedEvaluationT1,
}) {
  const birthday = patient?.birthday;
  const weightKg = patient?.weight;
  const heightCm = patient?.height;

  const age = calcAge(birthday);
  const bmi = calcBMI(weightKg, heightCm);

  const buildEvaluationStatus = (realizedDate, plannedDate) => {
    const realized = formatDate(realizedDate);
    if (realized) {
      return t("stats_eval_done", { date: realized, defaultValue: "Completed ({{date}})" });
    }

    const planned = formatDate(plannedDate);
    if (!planned) return "";

    const plannedDay = dayjs(plannedDate).startOf("day");
    const today = dayjs().startOf("day");
    if (!plannedDay.isValid()) return "";

    if (plannedDay.isAfter(today)) {
      return t("stats_eval_upcoming", { date: planned, defaultValue: "Upcoming ({{date}})" });
    }
    return t("stats_eval_missing", { date: planned, defaultValue: "Missing ({{date}})" });
  };

  const t0Label = buildEvaluationStatus(evaluationT0, plannedEvaluationT0);
  const t1Label = buildEvaluationStatus(evaluationT1, plannedEvaluationT1);

  return (
    <Card>
      <p>
        <strong className="patient-name">
          {patient.firstname?.charAt(0).toUpperCase() + patient.firstname?.slice(1)}{" "}
          {patient.lastname?.charAt(0).toUpperCase() + patient.lastname?.slice(1)}
        </strong>
      </p>

      <div className="info-grid">
        {/* Colonne gauche */}
        <div className="info-col">
          <div className="info-row">
            <span className="info-label">{t("stats_info_age", "Age")}:</span>
            <span className="info-value">{age ?? t("info_not_available", "N/A")}</span>
          </div>

          <div className="info-row">
            <span className="info-label">{t("stats_info_bmi", "BMI")}:</span>
            <span className="info-value">{bmi ?? t("info_not_available", "N/A")}</span>
          </div>

          <div className="info-row">
            <span className="info-label">{t("stats_info_status", "Status")}:</span>
            <span className="info-value">{formatPatientStatus(patient.status, t)}</span>
          </div>
        </div>

        {/* Colonne droite */}
        <div className="info-col info-col--right">
          <div className="info-row">
            <span className="info-label">{t("stats_info_programs", "Programs")}:</span>
            <span className="info-value">{programName || t("info_not_available", "N/A")}</span>
          </div>

          <div className="info-row">
            <span className="info-label">{t("stats_info_evaluation_t0", "Evaluation T0")}:</span>
            <span className="info-value">{t0Label || t("info_not_available", "N/A")}</span>
          </div>

          <div className="info-row">
            <span className="info-label">{t("stats_info_evaluation_t1", "Evaluation T1")}:</span>
            <span className="info-value">{t1Label || t("info_not_available", "N/A")}</span>
          </div>
        </div>
      </div>
    </Card>
  );
}

InfoCard.propTypes = {
  patient: PropTypes.object.isRequired,
  programName: PropTypes.string,
  t: PropTypes.func.isRequired,
  evaluationT0: PropTypes.any,
  evaluationT1: PropTypes.any,
  plannedEvaluationT0: PropTypes.any,
  plannedEvaluationT1: PropTypes.any,
};
